/**
 * Static brand assets (favicon / default logos).
 * R2 keys may 404 on staging — local public assets are the reliable fallback.
 */
import { getPublicFileUrl, STORAGE_OBJECT_KEYS } from '@/utils/storage-public-url.util'

/** Bundled icon in public/ — fallback if CDN favicon fails to load. */
export const LOCAL_FAVICON_HREF = '/landing/logo-icon-header.svg'

/** Platform favicon on R2 (Chrome tab). */
export const PLATFORM_FAVICON_HREF =
  'https://vidhyantra-bucket.vidhyantra.com/platform/vidyantra_logos/fav_icon.png'

/** Default wordmark for light surfaces (navbars, login, get started). */
export const DEFAULT_LOGO_LIGHT_HREF = getPublicFileUrl(STORAGE_OBJECT_KEYS.branding.lightLogo)

/** Default wordmark for dark surfaces (footer, dark theme). */
export const DEFAULT_LOGO_DARK_HREF = getPublicFileUrl(STORAGE_OBJECT_KEYS.branding.darkLogo)

/** Landing / public / login / get-started navbar wordmark size. */
export const PUBLIC_NAVBAR_LOGO_CLASS =
  'h-auto max-h-11 w-auto max-w-[220px] object-contain object-left'

/** Compact wordmark for org, branch, and platform admin top bars. */
export const ADMIN_NAVBAR_LOGO_CLASS =
  'h-auto max-h-7 w-auto max-w-[140px] object-contain object-left'

/** Fallback when R2/CDN favicon is missing (verified 200). */
export const DEFAULT_FAVICON_HREF = DEFAULT_LOGO_LIGHT_HREF

export function getDefaultLogoHref(mode: 'light' | 'dark' = 'light'): string {
  return mode === 'dark' ? DEFAULT_LOGO_DARK_HREF : DEFAULT_LOGO_LIGHT_HREF
}

/** True when the URL is a built-in Vidhyantra default (not a settings upload). */
export function isDefaultLogoHref(url: string | null | undefined): boolean {
  if (!url) return true
  return (
    url.includes('Light_mode_logo') ||
    url.includes('Dark_Mode_logo') ||
    url.includes('Group_17') ||
    url.includes('For_light_mode') ||
    url.includes('LOGO_jdcui6')
  )
}

export function getPreferredFaviconHref(): string {
  const fromEnv = getPublicFileUrl(STORAGE_OBJECT_KEYS.branding.favicon)
  return fromEnv && !fromEnv.startsWith('/') ? fromEnv : PLATFORM_FAVICON_HREF
}

/** Set document favicon; falls back to local SVG then Cloudinary if R2 URL fails. */
export function applyDocumentFavicon(): void {
  if (typeof document === 'undefined') return

  const primary = getPreferredFaviconHref()
  const fallbacks = [LOCAL_FAVICON_HREF, DEFAULT_FAVICON_HREF]

  const ensureLink = (): HTMLLinkElement => {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    return link
  }

  const setHref = (href: string, type?: string) => {
    const link = ensureLink()
    link.href = href
    link.type = type ?? (href.endsWith('.svg') ? 'image/svg+xml' : 'image/png')
  }

  let fallbackIdx = 0
  const tryNext = () => {
    const next = fallbacks[fallbackIdx]
    if (!next) return
    fallbackIdx += 1
    setHref(next)
    const probe = new Image()
    probe.onload = () => setHref(next)
    probe.onerror = tryNext
    probe.src = next
  }

  setHref(primary)
  const probe = new Image()
  probe.onload = () => setHref(primary)
  probe.onerror = tryNext
  probe.src = primary
}
